import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Make sure this is installed

// Component to render a single instance of your desired wave shape
interface MovingWaveProps {
  color: string;
}

const MovingWave = ({ color }: MovingWaveProps) => (
  // We make the SVG width large (e.g., 200vw) so we can scroll it smoothly
  <motion.svg
    className="h-full" 
    width="100vw" // Set a consistent width for the wave to scroll
    viewBox="0 0 1536 320"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="none" // Stretch to fill the height of its container
  >
    <path
      // Continuous wave path that loops seamlessly
      // The pattern repeats every 192 units, so 1536 (192*8) ensures perfect alignment
      d="M0,96 Q96,0 192,96 T384,96 T576,96 T768,96 T960,96 T1152,96 T1344,96 T1536,96"
      stroke={color}
      strokeWidth="1" // Reduced stroke width for a subtle background effect
      fill="none"
      vectorEffect="non-scaling-stroke"
    />
  </motion.svg>
);

const Footer = () => {
  return (
    <footer className="bg-navy relative overflow-hidden text-white">
      
      {/* 🌊 Animated Wave Background Container */}
      {/* The container is 200% wide to hold two waves for a seamless scroll */}
      <div className="absolute inset-0 h-full w-[200%] opacity-20 z-0">
        
        {/* The motion.div holds two copies of the wave and scrolls them */}
        <motion.div
          className="flex h-full"
          animate={{
            x: ['0%', '-100%'], // Move the wrapper from 0% to -100% of its width
          }}
          transition={{
            x: {
              duration: 25, // Time for one cycle (adjust for speed)
              ease: 'linear',
              repeat: Infinity,
            },
          }}
        >
          {/* Wave 1: Faint white stroke */}
          <div className="w-1/2 flex-shrink-0">
             <MovingWave color="#F1F5F9" /> 
          </div>
          
          {/* Wave 2: Sits next to wave 1 and seamlessly continues the pattern */}
          <div className="w-1/2 flex-shrink-0">
             <MovingWave color="#F1F5F9" /> 
          </div>
          
        </motion.div>
      </div>

      {/* Footer Content Layer (pt-12 to push content down from the wave) */}
      <div className="relative z-10 pt-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"> 
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* ... (Footer content remains the same) ... */}
          <div>
            <h3 className="text-lg font-bold mb-3 font-heading">AlonForge Solutions</h3>
            <p className="text-blue-200 text-sm">
              Forging your freelance wave with modern IT systems and vibrant UI portfolios.
            </p>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-3 font-heading">Quick Links</h4>
            <ul className="space-y-1.5">
              <li><Link to="/" className="text-blue-200 hover:text-white transition-colors text-sm">Home</Link></li>
              <li><Link to="/services" className="text-blue-200 hover:text-white transition-colors text-sm">Services</Link></li>
              <li><Link to="/systems" className="text-blue-200 hover:text-white transition-colors text-sm">Systems</Link></li>
              <li><Link to="/catalog" className="text-blue-200 hover:text-white transition-colors text-sm">Catalog</Link></li>
              <li><Link to="/about" className="text-blue-200 hover:text-white transition-colors text-sm">About</Link></li>
              <li><Link to="/contact" className="text-blue-200 hover:text-white transition-colors text-sm">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-3 font-heading">Connect</h4>
            <div className="flex space-x-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors text-sm">Facebook</a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors text-sm">Twitter</a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors text-sm">LinkedIn</a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-blue-800 text-center text-blue-200 text-sm">
          <p>&copy; {new Date().getFullYear()} AlonForge Solutions. Forging Filipino IT Waves Since 2025.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;